import { useEffect, useRef, useState } from 'react';

interface StatItem {
  target: number;
  suffix: string;
  label: string;
  accentColor: string;
}

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        setCount(target);
      }
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);

  return count;
}

function StatCard({ stat, animate }: { stat: StatItem; animate: boolean }) {
  const count = useCountUp(stat.target, 1800, animate);
  const isBlue = stat.accentColor === 'blue';

  return (
    <div className="flex flex-col items-center text-center px-6 py-10 group cursor-default">
      <div
        className={`text-6xl lg:text-7xl font-extrabold mb-3 transition-transform duration-300 group-hover:scale-110 ${
          isBlue ? 'text-blue-600' : 'text-orange-500'
        }`}
        style={{ fontFamily: '\'Inter\', \'Poppins\', sans-serif' }}
      >
        {count}{stat.suffix}
      </div>
      <div className="w-10 h-1 rounded-full mx-auto mb-3" style={{ background: isBlue ? '#2563eb' : '#f97316' }}></div>
      <p className="text-gray-500 text-base font-medium tracking-wide uppercase text-sm">
        {stat.label}
      </p>
    </div>
  );
}

export default function StatsCounter() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [animate, setAnimate] = useState(false);

  const stats: StatItem[] = [
    { target: 15, suffix: '+', label: 'Years of Experience', accentColor: 'blue' },
    { target: 50, suffix: '+', label: 'Countries Served', accentColor: 'orange' },
    { target: 1000, suffix: '+', label: 'Happy Clients', accentColor: 'blue' },
    { target: 99, suffix: '%', label: 'Quality Assurance', accentColor: 'orange' },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="pt-4 pb-12 lg:pb-16 bg-white border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-100">
          {stats.map((stat, index) => (
            <StatCard key={index} stat={stat} animate={animate} />
          ))}
        </div>
      </div>
    </section>
  );
}
